import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useNamespacedNavigate } from '@frontend/hooks';

import {
  DirectoryBrowseResponse,
  getAgentDirectoryDetails,
  getAgentsFromDirectory,
  installAgentFromDirectory,
  InstallAgentRequest,
  InstallAgentResponse,
  PublishAgentRequest,
  publishAgentToDirectory,
} from '../services/agent-directory';

export function usePushToAgentDirectory(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error, data } = useMutation({
    mutationFn: async ({ agentId, request }: { agentId: string; request: PublishAgentRequest }) => {
      return publishAgentToDirectory(agentId, request)();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opal:directory-agents'] });
      toaster.create('Agent published to directory successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to publish agent to directory', { type: 'danger' });
    },
  });

  return { mutate, isPending, error, data };
}

export function useInstallAgentFromDirectory(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const navigate = useNamespacedNavigate();

  const { mutate, isPending, error, data } = useMutation({
    mutationFn: async ({ agentId, request }: { agentId: string; request: InstallAgentRequest }) => {
      return installAgentFromDirectory(agentId, request)();
    },
    onSuccess: (response: InstallAgentResponse) => {
      queryClient.invalidateQueries({ queryKey: ['opal:specialized-agents'] });
      queryClient.invalidateQueries({ queryKey: ['opal:workflow-agents'] });

      // Navigate to the installed agent page
      if (response.agent_id) {
        toaster.create('Agent installed successfully! Redirecting...', { type: 'success' });
        navigate(`/agents/by-agent-id/${response.agent_id}`);
      } else {
        toaster.create('Agent installed successfully', { type: 'success' });
      }

      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to install agent', { type: 'danger' });
    },
  });

  return { mutate, isPending, error, data };
}

export function useGetAgentsFromDirectory({
  tags,
  is_featured,
  search,
  page = 1,
  limit = 20,
}: {
  tags?: string[];
  is_featured?: boolean;
  search?: string;
  page?: number;
  limit?: number;
} = {}) {
  const { data, isPending, error } = useQuery<DirectoryBrowseResponse>({
    queryKey: getAgentsFromDirectory.key(tags, is_featured, search, page, limit),
    queryFn: getAgentsFromDirectory({ tags, is_featured, search, page, limit }),
  });

  return {
    agents: data?.agents ?? [],
    pagination: data?.pagination,
    isPending,
    error,
  };
}

export function useAgentDirectoryDetails(agentId: string | undefined, directoryVersion?: number) {
  return useQuery({
    queryKey: getAgentDirectoryDetails.key(agentId || '', directoryVersion),
    queryFn: getAgentDirectoryDetails(agentId || '', directoryVersion),
    enabled: !!agentId,
    retry: false, // Don't retry on 404s
  });
}
